import React from 'react';
import { useSearchParams } from 'react-router';
import {
  useMyOrganizations,
  useAuthorizeAgent,
  ApiError,
  type OrgSwitcherItem,
} from '@redbax/shared';

type Phase = 'loading' | 'confirm' | 'submitting' | 'error' | 'denied';

/**
 * Query params the agent's tray launcher sends when opening the browser:
 *
 *   /agent/authorize?hostname=...&platform=...&machine_guid=...&mac_address=...
 *                    &code_challenge=...&callback=...&state=...
 *
 * `callback` is the loopback URI (http://127.0.0.1:<port>/callback).
 * `state` is round-tripped to the callback for CSRF protection.
 * `code_challenge` is the PKCE S256 challenge.
 */
interface AgentParams {
  hostname: string;
  platform: string;
  machineGuid?: string;
  macAddress?: string;
  codeChallenge: string;
  callback: string;
  state: string;
}

function parseParams(sp: URLSearchParams): AgentParams | null {
  const hostname = sp.get('hostname');
  const codeChallenge = sp.get('code_challenge');
  const callback = sp.get('callback');
  const state = sp.get('state');

  if (!hostname || !codeChallenge || !callback || !state) return null;

  return {
    hostname,
    platform: sp.get('platform') ?? '',
    machineGuid: sp.get('machine_guid') ?? undefined,
    macAddress: sp.get('mac_address') ?? undefined,
    codeChallenge,
    callback,
    state,
  };
}

function isLoopbackCallback(url: string): boolean {
  try {
    const parsed = new URL(url);
    return parsed.hostname === '127.0.0.1' || parsed.hostname === 'localhost';
  } catch {
    return false;
  }
}

export function AgentAuthorizePage() {
  const [searchParams] = useSearchParams();
  const params = React.useMemo(() => parseParams(searchParams), [searchParams]);

  const { data: organizations, isLoading: orgsLoading } = useMyOrganizations();
  const authorize = useAuthorizeAgent();

  const [selectedOrgId, setSelectedOrgId] = React.useState<string | null>(null);
  const [phase, setPhase] = React.useState<Phase>('loading');
  const [errorMessage, setErrorMessage] = React.useState<string>('');

  // Filter to active orgs only — you can't authorize a device into a pending/suspended org.
  const activeOrgs = React.useMemo(
    () => (organizations ?? []).filter((o) => o.status === 'Active'),
    [organizations],
  );

  // Auto-select when there's exactly one active org.
  React.useEffect(() => {
    if (orgsLoading || phase !== 'loading') return;

    if (!params) {
      setErrorMessage('Missing required parameters. Please sign in from the Redbax agent tray.');
      setPhase('error');
      return;
    }

    if (!isLoopbackCallback(params.callback)) {
      setErrorMessage('Invalid callback URL. Authorization can only redirect to a local agent.');
      setPhase('error');
      return;
    }

    if (activeOrgs.length === 0) {
      setErrorMessage('You do not have any active organizations. Please contact your administrator.');
      setPhase('error');
      return;
    }

    if (activeOrgs.length === 1) {
      setSelectedOrgId(activeOrgs[0].orgId);
    }

    setPhase('confirm');
  }, [orgsLoading, activeOrgs, params, phase]);

  const handleApprove = async () => {
    if (!params || !selectedOrgId) return;

    setPhase('submitting');
    try {
      const result = await authorize.mutateAsync({
        hostname: params.hostname,
        platform: params.platform,
        machineGuid: params.machineGuid,
        macAddress: params.macAddress,
        orgId: selectedOrgId,
        codeChallenge: params.codeChallenge,
      });

      // Redirect to the agent's loopback callback with the authorization code.
      const callbackUrl = new URL(params.callback);
      callbackUrl.searchParams.set('code', result.code);
      callbackUrl.searchParams.set('state', params.state);
      window.location.assign(callbackUrl.toString());
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setErrorMessage('You are not authorized to add devices to this organization, or the organization is not active.');
      } else {
        setErrorMessage('Something went wrong. Please try again from the Redbax agent tray.');
      }
      setPhase('error');
    }
  };

  const handleDeny = () => {
    if (!params) return;
    setPhase('denied');

    // Redirect to the loopback with an error so the agent knows it was denied.
    const callbackUrl = new URL(params.callback);
    callbackUrl.searchParams.set('error', 'access_denied');
    callbackUrl.searchParams.set('state', params.state);
    window.setTimeout(() => window.location.assign(callbackUrl.toString()), 1200);
  };

  const selectedOrg = activeOrgs.find((o) => o.orgId === selectedOrgId);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background">
      <div className="w-full max-w-md rounded-xl border border-border bg-surface p-8">
        {/* Header */}
        <div className="mb-6 text-center">
          <div className="mb-3 text-4xl">🔑</div>
          <h1 className="text-2xl font-semibold text-text-primary">Authorize device</h1>
          <p className="mt-1 text-sm text-text-secondary">
            A Redbax agent is requesting access to your organization.
          </p>
        </div>

        {(phase === 'loading' || orgsLoading) && (
          <div className="py-8 text-center text-sm text-text-secondary">
            Loading…
          </div>
        )}

        {phase === 'error' && (
          <div className="text-center">
            <div className="mb-4 text-4xl">⚠️</div>
            <p className="mb-6 text-sm text-text-secondary">{errorMessage}</p>
          </div>
        )}

        {phase === 'denied' && (
          <div className="text-center">
            <div className="mb-4 text-4xl">🚫</div>
            <p className="mb-2 text-sm font-medium text-text-primary">Authorization denied</p>
            <p className="mb-6 text-sm text-text-secondary">
              Redirecting back to the agent…
            </p>
          </div>
        )}

        {(phase === 'confirm' || phase === 'submitting') && params && (
          <>
            {/* Device info */}
            <div className="mb-6 rounded-lg border border-border bg-background p-4">
              <h2 className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                Device
              </h2>
              <dl className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <dt className="text-text-secondary">Hostname</dt>
                  <dd className="font-mono text-text-primary">{params.hostname}</dd>
                </div>
                {params.platform && (
                  <div className="flex justify-between">
                    <dt className="text-text-secondary">Platform</dt>
                    <dd className="text-text-primary">{params.platform}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Org picker — only if >1 active org */}
            {activeOrgs.length > 1 && (
              <div className="mb-6">
                <label
                  htmlFor="org-select"
                  className="mb-2 block text-xs font-semibold uppercase tracking-wider text-text-secondary"
                >
                  Organization
                </label>
                <select
                  id="org-select"
                  value={selectedOrgId ?? ''}
                  onChange={(e) => setSelectedOrgId(e.target.value || null)}
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm text-text-primary focus:border-red-accent focus:outline-none focus:ring-1 focus:ring-red-accent"
                >
                  <option value="">Select an organization…</option>
                  {activeOrgs.map((org) => (
                    <option key={org.orgId} value={org.orgId}>
                      {org.name} ({org.role})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Single-org confirmation */}
            {activeOrgs.length === 1 && selectedOrg && (
              <div className="mb-6 rounded-lg border border-border bg-background p-4">
                <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-secondary">
                  Organization
                </h2>
                <p className="text-sm font-medium text-text-primary">{selectedOrg.name}</p>
                <p className="text-xs text-text-secondary">{selectedOrg.role}</p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={handleDeny}
                disabled={phase === 'submitting'}
                className="flex-1 rounded-lg border border-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-background disabled:opacity-50"
              >
                Deny
              </button>
              <button
                onClick={handleApprove}
                disabled={phase === 'submitting' || !selectedOrgId}
                className="flex-1 rounded-lg bg-red-accent px-4 py-2 text-sm font-medium text-text-primary hover:bg-red-hover disabled:opacity-50"
              >
                {phase === 'submitting' ? 'Authorizing…' : 'Approve'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
